import React, { useState, useEffect } from "react";
import {
    FaTimes,
    FaSave,
    FaExclamationCircle,
    FaTools,
} from "react-icons/fa";

export const TicketUpdateProgressAction = ({ isOpen, onClose, ticket, onSave }) => {
    // State untuk Form
    const [activeTab, setActiveTab] = useState("status"); // status | schedule | work
    const [loading, setLoading] = useState(false);

    // Form Data
    const [formData, setFormData] = useState({
        status: "",
        scheduleDate: "",
        scheduleTime: "",
        scheduleReason: "", // Wajib jika reschedule
        workNote: "", // Catatan teknisi
        workProgress: 0,
    });

    // Reset form saat modal dibuka/tutup
    useEffect(() => {
        if (isOpen && ticket) {
            setFormData({
                status: ticket.status?.name || "",
                scheduleDate: "",
                scheduleTime: "",
                scheduleReason: "",
                workNote: "",
                workProgress: ticket.workDetails?.progress || 0,
            });
        }
    }, [isOpen, ticket]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Validasi Sederhana
        if (activeTab === "schedule" && (!formData.scheduleDate || !formData.scheduleReason)) {
            alert("Harap isi tanggal dan alasan penjadwalan ulang.");
            setLoading(false);
            return;
        }

        if (activeTab === "work" && !formData.workNote) {
            alert("Harap isi catatan pengerjaan.");
            setLoading(false);
            return;
        }

        // Simulasi Proses Simpan (API Call)
        console.log("Submitting Ticket Action:", formData);

        // Panggil fungsi callback dari parent (opsional)
        if (onSave) await onSave(formData);

        // Simulasi delay
        setTimeout(() => {
            setLoading(false);
            onClose();
            // Reset tab ke awal
            setActiveTab("status");
        }, 1000);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            {/* Backdrop Blur */}
            <div
                className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className="relative w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* --- Header --- */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800/50">
                    <div>
                        <h2 className="text-lg font-bold text-white flex items-center gap-2">
                            <FaTools className="text-cyan-400" />
                            Aksi Tiket: {ticket?.code}
                        </h2>
                        <p className="text-xs text-slate-400 mt-1">
                            Kelola status, jadwal, dan catatan pengerjaan
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white transition-colors p-2 rounded-full hover:bg-slate-700"
                    >
                        <FaTimes size={18} />
                    </button>
                </div>

                {/* --- Body Content (Scrollable) --- */}
                <div className="p-6 overflow-y-auto custom-scrollbar flex-1">

                    {/* TAB 1: UPDATE STATUS */}
                    {activeTab === "status" && (
                        <div className="space-y-4 animate-in fade-in duration-200">
                            <div className="p-4 bg-slate-800/40 rounded-xl border border-slate-700/50">
                                <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                                    Ubah Status Tiket
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-500 transition-colors"
                                >
                                    <option value="" disabled>Pilih Status...</option>
                                    <option value="Open">Open (Baru)</option>
                                    <option value="Process">Dalam Pengerjaan</option>
                                    <option value="Pending">Pending (Menunggu)</option>
                                    <option value="Resolved">Resolved (Selesai)</option>
                                    <option value="Closed">Closed (Tutup)</option>
                                </select>
                            </div>

                            {/* Info Tambahan */}
                            <div className="text-xs text-slate-500 flex items-start gap-2">
                                <FaExclamationCircle className="mt-0.5" />
                                <p>
                                    Mengubah status ke "Resolved" atau "Closed" akan menutup tiket ini.
                                    Pastikan semua pekerjaan sudah selesai.
                                </p>
                            </div>

                            {/* Current Status Info */}
                            <div className="mt-4 pt-4 border-t border-slate-700/50">
                                <p className="text-xs text-slate-500 mb-2">Status Saat Ini:</p>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                    {ticket?.status?.name || "Unknown"}
                                </div>
                            </div>


                            {/* TAB 3: WORK LOG */}
                            <div className="space-y-4 animate-in fade-in duration-200">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">
                                        Update Progress Pengerjaan (%)
                                    </label>
                                    <div className="flex items-center gap-4">
                                        <input
                                            type="range"
                                            min="0" max="100"
                                            value={formData.workProgress}
                                            onChange={(e) => setFormData({ ...formData, workProgress: e.target.value })}
                                            className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                                        />
                                        <span className="text-sm font-bold text-cyan-400 w-10 text-right">{formData.workProgress}%</span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-400 mb-2">
                                        Laporan Pengerjaan / Tindakan
                                    </label>
                                    <textarea
                                        rows="5"
                                        placeholder="Jelaskan apa yang sudah dikerjakan teknisi..."
                                        value={formData.workNote}
                                        onChange={(e) => setFormData({ ...formData, workNote: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-cyan-500 resize-none"
                                    ></textarea>
                                </div>

                                {/* Quick Actions Chips (Optional UX) */}
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase mb-2 font-bold">Cepat:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {["Pengecekan Kabel", "Ganti Konektor", "Restart ONU", "Selesai"].map((label) => (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, workNote: prev.workNote + (prev.workNote ? '\n' : '') + `- ${label}` }))}
                                                className="px-3 py-1 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-full text-[10px] text-slate-300 transition-colors"
                                            >
                                                + {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* --- Footer --- */}
                <div className="p-4 border-t border-slate-700 bg-slate-800/50 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-5 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors disabled:opacity-50"
                    >
                        Batal
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-500 shadow-lg shadow-cyan-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                                Menyimpan...
                            </>
                        ) : (
                            <>
                                <FaSave />
                                Simpan Perubahan
                            </>
                        )}
                    </button>
                </div>

            </div>
        </div>
    );
};